import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import { useAppSelector } from '../../../../reduxStore/reduxHooks';
import { deserify } from 'libs/shared-ui/src/commonUtils/serify';
import {
  GeneoKatex,
  getTeacherSubjectName,
  IconWrapper,
  IStyles,
  NoContentCard,
  pxToRem,
  pxTovW,
  QuestionContainerWithSolution,
  QuestionSourceTags,
} from '@geneo2-web/shared-ui';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Question } from '@protos/content_management/content.db_pb';
import {
  MultiSubjectSection,
  MultiSubjectSubSection,
  SectionQuestionMetaData,
  TaskInfo,
} from '@protos/learning_management/lms.db_pb';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AiGeneratedButton from '../../QuestionPaperGenerator/ReviewQuestionPaper/Buttons/AiGeneratedButton';
import { Timestamp } from '@bufbuild/protobuf';
import {
  formatMarksDisplay,
  getDisplayQuestionsForMeta,
  getGroupedQuestionHeaderLabel,
  getSubSectionMarksTotal,
  isPassageSectionEnabled,
  toRomanNumeral,
  getSubSectionAttemptAnyInstruction,
} from '../../utils/QPGv2.utils';
import { getCloudUploadPreviewSrc } from '../../../../utils/commonUtils';

const styles: IStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxTovW(15) },
    paddingX: { xs: pxToRem(20), md: pxTovW(50), lg: pxTovW(240) },
    paddingY: { xs: pxToRem(10), md: pxTovW(40) },
  },
  secondaryHeadingBox: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', md: 'center' },
    gap: { xs: pxToRem(10), md: pxTovW(15) },
  },
  ellipsisText: {
    marginTop: { xs: pxToRem(10), md: pxTovW(15) },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
  },
  viewAndPdfBox: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: { xs: pxToRem(10), md: pxTovW(30) },
    // ml: 'auto',
  },
  viewLibraryButton: {
    background: '#C1F8DA',
    display: 'flex',
    borderRadius: { xs: pxToRem(6), md: pxTovW(10) },
    boxSizing: 'border-box',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #0AA34F',
    padding: {
      xs: pxToRem(7),
      md: `${pxTovW(16)} ${pxTovW(23)}`,
      lg: `${pxTovW(13)} ${pxTovW(25)}`,
    },
    gap: { xs: pxToRem(5), md: pxTovW(16) },
    // height: { xs: pxToRem(32), md: pxTovW(70), lg: pxTovW(57) },
  },
  downloadPdfButton: {
    background: '#CFEAFF',
    display: 'flex',
    borderRadius: { xs: pxToRem(6), md: pxTovW(10) },
    boxSizing: 'border-box',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #007CDC',
    padding: {
      xs: pxToRem(7),
      md: `${pxTovW(16)} ${pxTovW(23)}`,
      lg: `${pxTovW(13)} ${pxTovW(25)}`,
    },
    gap: { xs: pxToRem(5), md: pxTovW(16) },
    // height: { xs: pxToRem(32), md: pxTovW(70), lg: pxTovW(57) },
  },

  sectionContainer: {
    marginTop: { xs: pxToRem(5), md: pxTovW(5) },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(30), md: pxTovW(60) },
  },
  subSectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(15), md: pxTovW(30) },
    marginTop: { xs: pxToRem(10), md: pxTovW(20) },
  },
  subjectSectionDetails: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  accordion: {
    border: '1px solid #E0E2E4',
    borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
    overflow: 'hidden',
    '&::before': {
      display: 'none',
    },
    '&:not(:last-of-type)': {
      borderBottom: 'none',
    },
    '&:first-of-type': {
      borderTopLeftRadius: { xs: pxToRem(15), md: pxTovW(15) },
      borderTopRightRadius: { xs: pxToRem(15), md: pxTovW(15) },
    },
    '&:last-of-type': {
      borderBottomLeftRadius: { xs: pxToRem(15), md: pxTovW(15) },
      borderBottomRightRadius: { xs: pxToRem(15), md: pxTovW(15) },
    },
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      margin: 0,
      padding: { xs: pxToRem(10), md: pxTovW(20) },
    },
  },
  subSectionDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(5), md: pxTovW(30) },
    marginTop: { xs: pxToRem(10), md: pxTovW(10) },
  },
  detailsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(5), md: pxTovW(5) },
  },
  accordionDetails: {
    paddingY: { xs: pxToRem(10), md: pxTovW(25) },
    paddingX: { xs: pxToRem(10), md: pxTovW(25) },
    borderTop: '2px solid #E0E2E4',
  },
  passageBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(12), md: pxTovW(16) },
    marginBottom: { xs: pxToRem(16), md: pxTovW(24) },
  },
  passageImage: {
    width: '100%',
    objectFit: 'cover',
    borderRadius: { xs: pxToRem(12), md: pxTovW(16) },
    display: 'block',
  },
  passageText: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: '#1D1D1D',
  },
  instructionText: {
    fontFamily: 'Poppins',
    marginTop: { xs: pxToRem(15), md: pxTovW(15) },
    marginBottom: { xs: pxToRem(40), md: pxTovW(40) },
    fontSize: { xs: pxToRem(12), md: 'unset' },
  },
};

export const QPGPreview = () => {
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const { qpg_content } = deserify(useAppSelector((state) => state.assessment));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(
    false
  );

  const sections = qpg_content?.multiSubjectSections ?? [];
  const assessmentContent = qpg_content?.assessmentContent ?? [];
  const questionTypeAndMarks = qpg_content?.questionTypeAndMarks ?? [];
  const questionsSequenceInfo = useMemo(
    () => new TaskInfo(qpg_content?.questionsSequenceInfo),
    [qpg_content?.questionsSequenceInfo]
  );
  const taskQuestionInfo = questionsSequenceInfo.taskQuestionInfo ?? [];

  // ----------------------------------------------------

  const questionMap = useMemo(() => {
    const map = new Map<string, Question>();
    assessmentContent.forEach((q) => {
      const question = new Question(q);
      if (question.questionId) map.set(String(question.questionId), question);
    });
    return map;
  }, [assessmentContent]);

  // ----------------------------------------------------

  const getMarksForQuestionId = (questionId: string): number => {
    const ques = questionMap.get(questionId);
    const taskInfo = taskQuestionInfo.find(
      (info) => info.questionId === questionId
    );
    const quesType = ques?.questionType;
    const foundQuesType = questionTypeAndMarks.find(
      (qType) => qType.questionType === quesType
    );
    if (foundQuesType) return foundQuesType.marks ?? 0;
    if (typeof taskInfo?.maxMarks === 'number') return taskInfo.maxMarks;
    const existing =
      ques?.question?.model?.value?.commonQuestionContent?.marks?.[0];
    if (typeof existing === 'number') return existing;
    return 0;
  };

  // ----------------------------------------------------

  /** Returns question from assessment content without applying marks (marks applied in render like QPGReviewPaperSection). */
  const getFormattedQuestionFromId = (id: string): Question | undefined => {
    const ques = questionMap.get(id);
    return ques ?? undefined;
  };

  // ----------------------------------------------------

  const subsectionTotalMarks = (subSection: MultiSubjectSubSection): number =>
    getSubSectionMarksTotal({
      subSection,
      getMarksForQuestionId,
    });

  // ----------------------------------------------------

  /** Returns global question number (1-based) from multiSubjectSections layout */
  const getQuestionNumberFromMultiSubject = (props: {
    sectionIndex: number;
    subSectionIndex: number;
    questionIndexInSubSection: number;
    multiSubjectSections: MultiSubjectSection[];
  }): number => {
    const {
      sectionIndex,
      subSectionIndex,
      questionIndexInSubSection,
      multiSubjectSections,
    } = props;
    let n = 0;
    for (let s = 0; s < sectionIndex; s++) {
      const section = multiSubjectSections[s];
      if (!section?.multiSubjectSubSections?.length) continue;
      for (const sub of section.multiSubjectSubSections) {
        n += sub.subSectionMetaData?.length ?? 0;
      }
    }
    const currentSection = multiSubjectSections[sectionIndex];
    if (currentSection?.multiSubjectSubSections) {
      for (let ss = 0; ss < subSectionIndex; ss++) {
        n +=
          currentSection.multiSubjectSubSections[ss]?.subSectionMetaData
            ?.length ?? 0;
      }
      n += questionIndexInSubSection;
    }
    return n + 1;
  };

  // ----------------------------------------------------

  /** Build questions array and question number for a subsection meta entry (main + alternates). Returns null if no questions. */
  const getQuestionsAndNumberForMeta = (params: {
    quesMeta: SectionQuestionMetaData;
    subSection: MultiSubjectSubSection;
    sectionIndex: number;
    subSectionIndex: number;
    index: number;
  }): { questions: Question[]; questionNumber: number } | null => {
    const { quesMeta, subSection, sectionIndex, subSectionIndex, index } =
      params;
    const allQuestionsMeta = getDisplayQuestionsForMeta(quesMeta);
    const questions = allQuestionsMeta
      .map((ques) => {
        const q = getFormattedQuestionFromId(ques.questionId);
        if (!q) return undefined;
        const subSectionMarks = subSection.questionTypeAndMarks?.find(
          (val) => val.questionType === q.questionType
        );
        if (q.question?.model?.value?.commonQuestionContent) {
          if (typeof ques.maxMarks === 'number') {
            q.question.model.value.commonQuestionContent.marks = [
              ques.maxMarks,
            ];
          } else if (subSectionMarks) {
            q.question.model.value.commonQuestionContent.marks = [
              subSectionMarks.marks ?? 0,
            ];
          } else {
            q.question.model.value.commonQuestionContent.marks = [
              getMarksForQuestionId(ques.questionId),
            ];
          }
          const taskInfo = taskQuestionInfo.find(
            (info) => info.questionId === ques.questionId
          );
          if (typeof taskInfo?.estimatedTimeInMin === 'number') {
            q.question.model.value.commonQuestionContent.time =
              taskInfo.estimatedTimeInMin * 60;
          }
        }
        return q;
      })
      .filter((q): q is Question => q != null);
    if (questions.length === 0) return null;
    const questionNumber = getQuestionNumberFromMultiSubject({
      sectionIndex: sectionIndex,
      subSectionIndex: subSectionIndex,
      questionIndexInSubSection: index,
      multiSubjectSections: sections,
    });
    return { questions, questionNumber };
  };

  // ----------------------------------------------------

  const viewLibraryHandler = () => {
    console.log('view library');
  };

  // ----------------------------------------------------

  const handleMenuClick = async (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.MouseEvent<HTMLImageElement, MouseEvent>
      | React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    {
      name: 'Download Question Paper',
      onClick: async () => {
        console.log('download question paper');
      },
    },
    {
      name: 'Download Answer Paper',
      onClick: async () => {
        console.log('download answer paper');
      },
    },
    {
      name: 'Download Both',
      onClick: async () => {
        console.log('download both');
      },
    },
  ];

  // ----------------------------------------------------

  const getTotalMarksOfSection = (section: MultiSubjectSection): number =>
    (section.multiSubjectSubSections ?? []).reduce(
      (sectionTotal, subSection) =>
        sectionTotal + subsectionTotalMarks(subSection),
      0
    );

  // ----------------------------------------------------

  const getSubjectName = (subjectId: number) =>
    getTeacherSubjectName(subjectId, user_info?.teachClassSubjects) ?? '';

  // ----------------------------------------------------

  return (
    <Box sx={styles.root}>
      <Typography variant="h1">Question Paper Generator</Typography>

      <Box sx={styles.secondaryHeadingBox}>
        <Typography variant="h2">Review Question Paper</Typography>

        {/* <Box sx={styles.viewAndPdfBox}>
          <Button sx={styles.viewLibraryButton} onClick={viewLibraryHandler}>
            <AddCircleOutlineIcon
              sx={{
                color: '#0AA34F',
                fontSize: { xs: pxToRem(18), md: pxTovW(30) },
              }}
            />
            <Typography variant="h3" fontWeight="bold" color="secondary.main">
              View Library
            </Typography>
          </Button>

          <Button sx={styles.downloadPdfButton} onClick={handleMenuClick}>
            <IconWrapper
              name="download-qpg-blue"
              type="png"
              parentFolder="icons"
              size="md"
            />
            <Typography variant="h3" fontWeight="bold" color="primary.main">
              Download PDF
            </Typography>
          </Button>
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          sx={styles.menu}
        >
          {menuItems.map((item, index) => (
            <MenuItem
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                handleMenuClose();
                item.onClick();
              }}
              sx={styles.menuItem}
            >
              <Typography
                variant="smallText"
                fontWeight="regular"
                sx={{ fontFamily: 'Poppins' }}
              >
                {item.name}
              </Typography>
            </MenuItem>
          ))}
        </Menu> */}
      </Box>

      {sections.some((section) => section.isAiGenerated) && (
        <Typography variant="h5" sx={{ fontFamily: 'Poppins' }}>
          <b>Note:</b> AI-generated questions may sometimes be inaccurate due to
          hallucinations. Please review and verify all questions before
          publishing to ensure accuracy.
        </Typography>
      )}

      <Typography
        variant="h2"
        textAlign="center"
        textTransform="uppercase"
        sx={styles.ellipsisText}
      >
        {qpg_content?.assessment?.assessmentTitle}
      </Typography>

      {qpg_content?.assessment?.assessmentInstructions &&
        qpg_content.assessment.assessmentInstructions.length > 0 &&
        qpg_content.assessment.assessmentInstructions.some(Boolean) && (
          <Box
            className="ql-editor"
            sx={styles.instructionText}
            dangerouslySetInnerHTML={{
              __html: new GeneoKatex(
                qpg_content.assessment.assessmentInstructions.join('')
              ).parsedHtml(),
            }}
          />
        )}

      <Box sx={styles.sectionContainer}>
        {sections.map((section, sectionIndex) => (
          <Box key={`section-${sectionIndex}-${section.sectionName}`}>
            <Box sx={styles.subjectSectionDetails}>
              {section.sectionTitle ? (
                <Typography
                  variant="h2"
                  color="primary.main"
                  sx={styles.ellipsisText}
                >
                  Section {sectionIndex + 1} -{' '}
                  {getSubjectName(section.subjectId)} - {section.sectionTitle}
                </Typography>
              ) : (
                <Typography
                  variant="h2"
                  color="primary.main"
                  sx={styles.ellipsisText}
                >
                  Section {sectionIndex + 1} -{' '}
                  {getSubjectName(section.subjectId)}
                </Typography>
              )}

              {getTotalMarksOfSection(section) > 0 && (
                <Typography
                  variant="h3"
                  color="secondary.main"
                  fontWeight="bold"
                  sx={{
                    marginLeft: 'auto',
                    marginTop: { xs: pxToRem(10), md: pxTovW(15) },
                  }}
                >
                  Total: {formatMarksDisplay(getTotalMarksOfSection(section))}{' '}
                  Marks
                </Typography>
              )}

              {section.instructions?.length > 0 &&
                section.instructions.some(Boolean) && (
                  <Typography variant="cardText" sx={styles.instructionText}>
                    {section.instructions.join('\n')}
                  </Typography>
                )}
            </Box>

            <Box sx={styles.subSectionContainer}>
              {!section.multiSubjectSubSections?.length ? (
                <NoContentCard
                  variant="info"
                  icon="qpg"
                  text="No Questions Added"
                />
              ) : (
                (section.multiSubjectSubSections ?? []).map(
                  (subSection, subSectionIndex) => {
                    const accordionKey = `${sectionIndex}-${subSectionIndex}`;
                    return (
                      <Accordion
                        key={`sub-${sectionIndex}-${subSectionIndex}-${subSection.subSectionName}`}
                        expanded={expandedAccordion === accordionKey}
                        onChange={() =>
                          setExpandedAccordion((prev) =>
                            prev === accordionKey ? false : accordionKey
                          )
                        }
                        sx={styles.accordion}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={styles.accordionSummary}
                        >
                          <Box>
                            <Typography
                              variant="h2"
                              sx={{
                                fontSize: { xs: pxToRem(24), md: pxTovW(26) },
                              }}
                            >
                              {subSection.subSectionName}
                            </Typography>
                            <Box sx={styles.subSectionDetails}>
                              <Box sx={styles.detailsContainer}>
                                <IconWrapper
                                  name="questions"
                                  size="md"
                                  type="png"
                                  parentFolder="icons"
                                />
                                <Typography variant="h4">
                                  Total No of Questions:{' '}
                                  <b>
                                    {subSection.subSectionMetaData?.length ?? 0}
                                  </b>
                                </Typography>
                              </Box>
                              <Box sx={styles.detailsContainer}>
                                <IconWrapper
                                  name="percentage-primary"
                                  size="md"
                                  type="png"
                                  parentFolder="icons"
                                />
                                <Typography variant="h4">
                                  Total Marks:{' '}
                                  <b>
                                    {formatMarksDisplay(
                                      subsectionTotalMarks(subSection)
                                    )}
                                  </b>
                                </Typography>
                              </Box>
                              {subSection.attemptAnyCount !== undefined ? (
                                <Typography variant="h4">
                                  ( Attempt any:{' '}
                                  <b>{subSection.attemptAnyCount}</b> )
                                </Typography>
                              ) : null}
                            </Box>
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={styles.accordionDetails}>
                          {(() => {
                            const isPassage =
                              isPassageSectionEnabled(subSection);
                            const passageImageSrc = getCloudUploadPreviewSrc(
                              subSection.passageImagesUpload?.[0]
                            );
                            const passageBody = (subSection.passageText ?? [])
                              .map((t) => t?.trim())
                              .filter(Boolean)
                              .join('\n\n');
                            if (
                              !isPassage ||
                              (!passageImageSrc && !passageBody)
                            ) {
                              return null;
                            }
                            return (
                              <Box sx={styles.passageBlock}>
                                {passageBody ? (
                                  <Typography
                                    variant="h4"
                                    sx={styles.passageText}
                                  >
                                    {passageBody}
                                  </Typography>
                                ) : null}
                                {passageImageSrc ? (
                                  <Box
                                    component="img"
                                    src={passageImageSrc}
                                    alt="Passage"
                                    sx={styles.passageImage}
                                  />
                                ) : null}
                              </Box>
                            );
                          })()}
                          {subSection.attemptAnyCount !== undefined ? (
                            <Typography
                              variant="h4"
                              sx={{
                                fontFamily: 'Poppins',
                                fontWeight: 600,
                                marginBottom: {
                                  xs: pxToRem(8),
                                  md: pxTovW(8),
                                },
                              }}
                            >
                              {getSubSectionAttemptAnyInstruction(
                                subSection.attemptAnyCount
                              )}
                            </Typography>
                          ) : null}
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: { md: pxTovW(40), xs: pxToRem(15) },
                            }}
                          >
                            {(subSection.subSectionMetaData ?? [])
                              .sort(
                                (a, b) =>
                                  (a?.sequence ?? 0) - (b?.sequence ?? 0)
                              )
                              .map((quesMeta, index) => {
                                const result = getQuestionsAndNumberForMeta({
                                  quesMeta,
                                  subSection,
                                  sectionIndex,
                                  subSectionIndex,
                                  index,
                                });
                                if (!result) return null;
                                const { questions, questionNumber } = result;
                                const isGrouped = !!quesMeta.isGrouped;
                                const groupedHeaderLabel = isGrouped
                                  ? getGroupedQuestionHeaderLabel({
                                      questionNumber,
                                      meta: quesMeta,
                                      questionType:
                                        quesMeta.questionType ??
                                        questions[0]?.questionType,
                                    })
                                  : undefined;
                                return (
                                  <Box key={quesMeta.questionId}>
                                    <Box
                                      sx={{
                                        ...(questions.length > 1 && {
                                          border: `1px solid #007CDC`,
                                          borderRadius: {
                                            xs: pxToRem(20),
                                            md: pxTovW(20),
                                          },
                                          padding: {
                                            xs: `${pxTovW(14)} ${pxTovW(10)}`,
                                            md: `${pxTovW(18)} ${pxTovW(13)}`,
                                          },
                                          backgroundColor: '#F5F5FF',
                                        }),
                                        display: 'flex',
                                        flexDirection: 'column',
                                      }}
                                    >
                                      {isGrouped && groupedHeaderLabel ? (
                                        <Typography
                                          variant="h5"
                                          fontWeight="bold"
                                          sx={{
                                            fontSize: {
                                              xs: pxToRem(16),
                                              md: pxTovW(24),
                                            },
                                            marginBottom: {
                                              xs: pxToRem(10),
                                              md: pxTovW(10),
                                            },
                                          }}
                                        >
                                          {groupedHeaderLabel}
                                        </Typography>
                                      ) : null}
                                      {questions.map((question, altIdx) => (
                                        <React.Fragment
                                          key={question.questionId}
                                        >
                                          <QuestionContainerWithSolution
                                            questionNumber={
                                              isGrouped
                                                ? toRomanNumeral(altIdx + 1)
                                                : questionNumber
                                            }
                                            question={question}
                                            showQuestionStats
                                            showAnswer
                                            imageResizeEnabled
                                            headerLeftElement={
                                              <>
                                                {question.isAiGenerated ? (
                                                  <AiGeneratedButton />
                                                ) : null}
                                                <QuestionSourceTags
                                                  question={question}
                                                  teacherProfileId={
                                                    user_info?.teacherProfileId
                                                  }
                                                  teacherName={
                                                    user_info?.userName
                                                  }
                                                />
                                              </>
                                            }
                                          />
                                          {altIdx < questions.length - 1 &&
                                            !quesMeta.isGrouped &&
                                            renderOrBox()}
                                        </React.Fragment>
                                      ))}
                                    </Box>
                                  </Box>
                                );
                              })}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    );
                  }
                )
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ================================================================

const renderOrBox = () => (
  <Box sx={{ px: { xs: pxToRem(10), md: pxTovW(20) } }}>
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          backgroundColor: '#DDF0FF',
          color: '#000',
          padding: '10px',
          border: '1px solid #007CDC',
          borderRadius: '50%',
          zIndex: 1,
        }}
      >
        OR
      </Box>
      <Box
        sx={{
          height: '1px',
          width: '100%',
          backgroundColor: '#007CDC',
          position: 'absolute',
          top: '50%',
          left: '0',
          right: '0',
          transform: 'translateY(-50%)',
        }}
      />
    </Box>
  </Box>
);

// ================================================================
